import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, AlertTriangle, Sparkles, Banknote, Brain, Trash2, History, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ReviewCard } from "./ReviewCard";
import { AutoSettingsBar } from "./AutoSettingsBar";
import { WeeklyDigestPanel } from "./WeeklyDigestPanel";

function Kpi({ label, value, hint, color, icon: Icon, testId }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4 relative overflow-hidden" data-testid={testId}>
      <div className="absolute -right-3 -top-3 w-16 h-16 rounded-full opacity-10" style={{ background: color }} />
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="text-3xl font-black mt-1" style={{ color }}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function SetupBadge({ ok, label }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${ok ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-300"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {label}
    </span>
  );
}

export function AutoReviewTab({ api, societeId, onCountChange }) {
  const [overview, setOverview] = useState(null);
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRules, setShowRules] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, r, a, ru] = await Promise.all([
        api.get("/accounting/auto/overview", { params: { societe_id: societeId } }),
        api.get("/accounting/auto/review", { params: { societe_id: societeId } }),
        api.get("/accounting/accounts", { params: { societe_id: societeId } }),
        api.get("/accounting/auto/rules", { params: { societe_id: societeId } }),
      ]);
      setOverview(o.data);
      setEntries(r.data);
      setAccounts((a.data || []).filter((x) => [4, 5, 6, 7].includes(x.classe)));
      setRules(ru.data);
      onCountChange?.(o.data.counts.to_review);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societeId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const approve = async (entry, accountNumber) => {
    try {
      await api.post(`/accounting/auto/review/${entry.entry_id}/approve`, accountNumber ? { account_number: accountNumber } : {});
      toast.success(accountNumber ? "Écriture corrigée et validée — règle mémorisée" : "Écriture validée");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const reject = async (entry) => {
    if (!window.confirm("Ignorer cette écriture ? La ligne bancaire sera marquée comme ignorée.")) return;
    try {
      await api.post(`/accounting/auto/review/${entry.entry_id}/reject`);
      toast.success("Écriture ignorée");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const deleteRule = async (id) => {
    try { await api.delete(`/accounting/auto/rules/${id}`); toast.success("Règle supprimée"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (loading || !overview) return <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-[#f7c935]" />;
  const { counts, setup, settings, last_run } = overview;
  const setupOk = setup.pcg_ok && setup.journals_ok && setup.fiscal_year_ok;

  return (
    <div className="space-y-4" data-testid="auto-review-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="À vérifier" value={counts.to_review} hint="validation en 1 clic" color="#f7c935" icon={AlertTriangle} testId="kpi-to-review" />
        <Kpi label="Validées automatiquement" value={counts.auto_validated} hint={`${counts.auto_validated_month} ce mois`} color="#22c55e" icon={ShieldCheck} testId="kpi-auto-validated" />
        <Kpi label="Lignes banque en attente" value={counts.bank_pending} hint={counts.bank_alerts > 0 ? `${counts.bank_alerts} au-delà de ${settings.bank_alert_days} j` : "aucune alerte"} color={counts.bank_alerts > 0 ? "#ef4444" : "#38bdf8"} icon={Banknote} testId="kpi-bank-pending" />
        <Kpi label="Règles apprises" value={counts.rules} hint="reconnaissance bancaire" color="#a855f7" icon={Brain} testId="kpi-rules" />
      </div>

      <AutoSettingsBar api={api} societeId={societeId} settings={settings} toReview={counts.to_review} onChanged={load} onRun={load} onApproveAll={load} />

      <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500" data-testid="auto-setup-status">
        <SetupBadge ok={setup.pcg_ok} label="Plan comptable" />
        <SetupBadge ok={setup.journals_ok} label="Journaux" />
        <SetupBadge ok={setup.fiscal_year_ok} label="Exercice ouvert" />
        {!setupOk && <span className="text-amber-300">« Lancer la génération » initialise automatiquement la comptabilité de cette société.</span>}
        <div className="flex-1" />
        {last_run && (
          <span className="flex items-center gap-1" data-testid="auto-last-run">
            <History className="w-3 h-3" /> Dernière exécution {new Date(last_run.finished_at).toLocaleString("fr-FR")} ({last_run.trigger === "scheduler" ? "auto" : "manuelle"})
          </span>
        )}
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#f7c935]" /> Écritures à vérifier ({entries.length})
        </h3>
        {entries.length === 0 ? (
          <div className="text-center py-12 text-slate-500 bg-[#0b0811] border border-dashed border-white/10 rounded-xl" data-testid="review-empty">
            <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500/60" />
            Rien à vérifier — toutes les écritures générées ont été validées.
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((e) => <ReviewCard key={e.entry_id} entry={e} accounts={accounts} onApprove={approve} onReject={reject} />)}
          </div>
        )}
      </div>

      <WeeklyDigestPanel api={api} societeId={societeId} settings={settings} onChanged={load} />

      <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4" data-testid="rules-panel">
        <button onClick={() => setShowRules(!showRules)} className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400" data-testid="rules-toggle">
          <span className="flex items-center gap-2"><Brain className="w-3.5 h-3.5 text-purple-400" /> Règles de reconnaissance bancaire ({rules.length})</span>
          <span className="text-slate-600">{showRules ? "masquer" : "afficher"}</span>
        </button>
        {showRules && (
          <div className="mt-3 space-y-1">
            {rules.map((r) => (
              <div key={r.rule_id} className="flex items-center gap-3 text-xs py-1.5 border-t border-white/5" data-testid={`rule-${r.rule_id}`}>
                <span className="font-mono text-slate-300 flex-1 truncate">« {r.pattern} »</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{r.direction === "credit" ? "crédit" : "débit"}</span>
                <span className="font-mono text-[#f7c935]">{r.account_number}</span>
                <span className="text-slate-500 truncate max-w-[180px]">{r.account_label}</span>
                {r.vat_rate > 0 && <span className="text-[10px] text-slate-500">TVA {r.vat_rate}%</span>}
                <span className="text-[10px] text-slate-600">{r.times_used || 0}× utilisée</span>
                <button onClick={() => deleteRule(r.rule_id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`rule-delete-${r.rule_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {rules.length === 0 && <div className="text-xs text-slate-500 py-2">Aucune règle : validez une écriture bancaire pour que le moteur mémorise l'imputation.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
